import { useMutation, useQuery } from "@tanstack/react-query";

import {
  createHarnessOrder,
  getHarnessDiagnostics,
  getHarnessQueueStatus,
  runHarnessTransitions,
  type CreateHarnessOrderInput,
  type CreateHarnessOrderResponse,
  type HarnessDiagnosticsResponse,
  type RunHarnessTransitionsInput,
  type RunHarnessTransitionsResponse,
} from "#/api/medicationOrderHarness.api";
import { getUsersByParameters, type UserSearchParameters } from "#/api/user.api";
import type { UserRow } from "#/api/user.type";

export function useMedicationHarnessRun() {
  const createOrderMutation = useMutation<
    CreateHarnessOrderResponse,
    Error,
    CreateHarnessOrderInput
  >({
    mutationFn: createHarnessOrder,
  });

  const runTransitionsMutation = useMutation<
    RunHarnessTransitionsResponse,
    Error,
    { orderId: string; input: RunHarnessTransitionsInput }
  >({
    mutationFn: ({ orderId, input }) => runHarnessTransitions(orderId, input),
  });

  const diagnosticsMutation = useMutation<
    HarnessDiagnosticsResponse,
    Error,
    string
  >({
    mutationFn: getHarnessDiagnostics,
  });

  const userSearchMutation = useMutation<
    UserRow[],
    Error,
    UserSearchParameters
  >({
    mutationKey: ["medication-harness", "user-search"],
    mutationFn: getUsersByParameters,
  });

  const queueStatusQuery = useQuery({
    queryKey: ["harness-queue-status"],
    queryFn: getHarnessQueueStatus,
    retry: false,
  });

  const isRunning =
    createOrderMutation.isPending ||
    runTransitionsMutation.isPending ||
    diagnosticsMutation.isPending;

  return {
    createOrderMutation,
    runTransitionsMutation,
    diagnosticsMutation,
    userSearchMutation,
    queueStatusQuery,
    isRunning,
  };
}

export default useMedicationHarnessRun;
